import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Global } from 'src/global';
import { BaseService, ServiceResponse } from '../base.service';
import {
    WHATSAPP_CONVERSATION_ACCESS,
    WHATSAPP_EXTERNAL_HANDOFF_STATUS,
    WhatsappAssignableUser,
    WhatsappConversationDetailResponse,
    WhatsappConversationDetailFilter,
    WhatsappConversationExternalHandoffPayload,
    WhatsappConversationFilter,
    WhatsappConversationReplyPayload,
    WhatsappConversationReplyTemplate,
    WhatsappConversationSummary,
    WhatsappConversationMessage,
    WhatsappConversationEvent,
    getWhatsappExternalHandoffStatusLabel,
} from 'src/app/models/notificacion/whatsapp_conversation.model';
import { normalizeParaguayPhone } from 'src/app/utilities/phone-normalizer';
import { IPasoRegistroLectura } from 'src/app/models/notificacion/whatsapp_flujo.model';

@Injectable({
    providedIn: 'root'
})
export class WhatsappConversationService extends BaseService {

    recordName = WHATSAPP_CONVERSATION_ACCESS.RECORD_NAME;
    url = `${environment.host}/whatsapp-conversation`;
    codigoModulo = WHATSAPP_CONVERSATION_ACCESS.CODIGO_MODULO;
    codigoRegistro = WHATSAPP_CONVERSATION_ACCESS.CODIGO_REGISTRO;
    controlarTipoAcceso = false;

    private readonly templateUrl = `${environment.host}/whatsapp-template`;

    async listConversations(filters: WhatsappConversationFilter = {}): Promise<ServiceResponse> {
        try {
            await this.updateToken();
            const { params, requestFilters } = this.buildConversationRequest(filters);
            this.header['filters'] = JSON.stringify(requestFilters);
            const response = await lastValueFrom(this.http.get<ServiceResponse>(this.url, {
                headers: this.header,
                params,
            }));
            return response;
        } catch (error: unknown) {
            return this.checkError(error);
        } finally {
            delete this.header['filters'];
        }
    }

    async getConversationDetail(id: number, options?: number | WhatsappConversationDetailFilter): Promise<ServiceResponse> {
        try {
            await this.updateToken();
            const detailFilters = this.normalizeConversationDetailFilters(options);
            this.header['filters'] = JSON.stringify(this.buildConversationDetailRequest(detailFilters));
            const response = await lastValueFrom(this.http.get<ServiceResponse>(`${this.url}/${id}`, {
                headers: this.header,
            }));
            return response;
        } catch (error: unknown) {
            return this.checkError(error);
        } finally {
            delete this.header['filters'];
        }
    }

    async takeConversation(id: number, idEmpresa?: number): Promise<ServiceResponse> {
        return this.executeConversationAction(`${this.url}/${id}/take`, {
            id_empresa: idEmpresa || this.getCurrentUserCompanyId(),
            id_usuario: this.getCurrentUserId(),
        });
    }

    async assignConversation(id: number, assignedUserId: number, idEmpresa?: number): Promise<ServiceResponse> {
        return this.executeConversationAction(`${this.url}/${id}/assign`, {
            id_empresa: idEmpresa || this.getCurrentUserCompanyId(),
            id_usuario: this.getCurrentUserId(),
            assigned_user_id: assignedUserId,
        });
    }

    async updateConversationStatus(id: number, statusCode: number, idEmpresa?: number): Promise<ServiceResponse> {
        return this.executeConversationAction(`${this.url}/${id}/status`, {
            id_empresa: idEmpresa || this.getCurrentUserCompanyId(),
            id_usuario: this.getCurrentUserId(),
            status_code: statusCode,
        });
    }

    async replyConversation(id: number, payload: WhatsappConversationReplyPayload, idEmpresa?: number): Promise<ServiceResponse> {
        return this.executeConversationAction(`${this.url}/${id}/reply`, {
            id_empresa: idEmpresa || this.getCurrentUserCompanyId(),
            id_usuario: this.getCurrentUserId(),
            body_text: payload.body_text || '',
            media_url: payload.media_url || '',
            template_id: payload.template_id || null,
            template_code: payload.template_code || '',
            template_language: payload.template_language || '',
        });
    }

    async sendReactivationTemplate(id: number, idEmpresa?: number): Promise<ServiceResponse> {
        return this.executeConversationAction(`${this.url}/${id}/send-reactivation-template`, {
            id_empresa: idEmpresa || this.getCurrentUserCompanyId(),
            id_usuario: this.getCurrentUserId(),
        });
    }

    async externalHandoffConversation(id: number, payload: WhatsappConversationExternalHandoffPayload, idEmpresa?: number): Promise<ServiceResponse> {
        const normalizedTargetPhone = normalizeParaguayPhone(payload.target_phone) || String(payload.target_phone || '').trim();

        return this.executeConversationAction(`${this.url}/${id}/external-handoff`, {
            id_empresa: idEmpresa || this.getCurrentUserCompanyId(),
            id_usuario: this.getCurrentUserId(),
            target_phone: normalizedTargetPhone,
            responsible_name: payload.responsible_name,
            summary: payload.summary,
        });
    }

    async reopenConversation(id: number, idEmpresa?: number): Promise<ServiceResponse> {
        const companyId = idEmpresa || this.getCurrentUserCompanyId();
        const directResponse = await this.executeConversationAction(`${this.url}/${id}/reopen`, {
            id_empresa: companyId,
            id_usuario: this.getCurrentUserId(),
        });

        if (directResponse.ok) {
            return directResponse;
        }

        return this.updateConversationStatus(id, 1, companyId);
    }

    async listAvailableReplyTemplates(conversationId: number, idEmpresa?: number): Promise<ServiceResponse> {
        void conversationId;

        const companyId = idEmpresa || this.getCurrentUserCompanyId();
        const response = await this.executeGetRequest(`${this.templateUrl}/available`, {
            id_empresa: String(companyId),
        });

        if (response.ok) {
            return response;
        }

        return this.listLegacyApprovedTemplates(companyId);
    }

    /**
     * Registro de lo que el cliente respondió en el flujo de esta
     * conversación. Vive en el servicio de conversación —y no en uno propio—
     * porque el endpoint cuelga de la conversación: el flujo no es un recurso
     * aparte, es una columna de la plantilla.
     */
    async obtenerRegistroFlujoDeConversacion(idEmpresa: number, conversationId: number): Promise<ServiceResponse> {
        return this.executeGetRequest(`${this.url}/${conversationId}/flujo`, {
            id_empresa: String(idEmpresa || this.getCurrentUserCompanyId()),
        });
    }

    extraerRegistroFlujo(response: ServiceResponse): IPasoRegistroLectura[] {
        if (!response?.ok) {
            return [];
        }

        const datos = (response.resp as { pasos?: IPasoRegistroLectura[] })?.pasos ?? response.resp;

        return Array.isArray(datos) ? datos : [];
    }

    async listAssignableUsers(idEmpresa?: number): Promise<ServiceResponse> {
        try {
            await this.updateToken();
            const companyId = idEmpresa || this.getCurrentUserCompanyId();
            const currentUserId = this.getCurrentUserId();
            this.header['filters'] = JSON.stringify([
                { field: 'id_empresa', type: 'id', value: companyId },
                { field: 'id_usuario', type: 'id', value: currentUserId },
            ]);
            const response = await lastValueFrom(this.http.get<ServiceResponse>(`${this.url}/assignable-users`, {
                headers: this.header,
                params: {
                    page: 0,
                    size: Global.SIZE_GET_ALL,
                },
            }));
            delete this.header['filters'];
            return response;
        } catch (error: unknown) {
            delete this.header['filters'];
            return this.checkError(error);
        }
    }

    extractConversationList(resp: ServiceResponse): WhatsappConversationSummary[] {
        if (!resp?.ok) {
            return [];
        }

        return (resp.resp?.rows || []).map((row: Record<string, unknown>) => this.normalizeConversationSummary(row));
    }

    extractConversationDetail(resp: ServiceResponse): WhatsappConversationDetailResponse | null {
        if (!resp?.ok) {
            return null;
        }

        const response = (resp.resp || {}) as Record<string, unknown>;
        const conversation = this.normalizeConversationSummary((response['conversation'] || {}) as Record<string, unknown>);
        const availableReplyTemplates = this.extractReplyTemplatesFromRaw(
            response['available_reply_templates']
            || response['availableTemplates']
            || conversation.available_reply_templates
            || []
        );

        return {
            conversation: {
                ...conversation,
                available_reply_templates: availableReplyTemplates,
            },
            messages: ((response['messages'] || []) as Record<string, unknown>[]).map((message) => this.normalizeConversationMessage(message)),
            events: ((response['events'] || []) as Record<string, unknown>[]).map((event) => this.normalizeConversationEvent(event)),
            available_reply_templates: availableReplyTemplates,
        };
    }

    extractAssignableUsers(resp: ServiceResponse): WhatsappAssignableUser[] {
        if (!resp?.ok) {
            return [];
        }

        return ((resp.resp?.rows || []) as WhatsappAssignableUser[])
            .filter((item) => item.activo !== false)
            .map((item) => this.normalizeAssignableUser(item))
            .sort((first, second) => String(first.display_label || '').localeCompare(String(second.display_label || '')));
    }

    extractReplyTemplates(resp: ServiceResponse): WhatsappConversationReplyTemplate[] {
        if (!resp?.ok) {
            return [];
        }

        return this.extractReplyTemplatesFromRaw(
            resp.resp?.rows
            || resp.resp?.templates
            || resp.resp?.data
            || resp.resp
            || []
        );
    }

    private async executeConversationAction(url: string, body: Record<string, unknown>): Promise<ServiceResponse> {
        try {
            await this.updateToken();
            delete this.header['filters'];
            const response = await lastValueFrom(this.http.post<ServiceResponse>(url, body, { headers: this.header }));
            return this.normalizeConversationActionResponse(response);
        } catch (error: unknown) {
            const response = await this.checkError(error);
            return this.normalizeConversationActionResponse(response);
        }
    }

    private normalizeConversationActionResponse(response: ServiceResponse): ServiceResponse {
        if (response?.ok !== false) {
            return response;
        }

        const humanMessage = this.getHumanConversationErrorMessage(response.resp || response.msg);
        if (!humanMessage) {
            return response;
        }

        return {
            ...response,
            msg: 'Conversación vencida',
            resp: humanMessage,
        };
    }

    private getHumanConversationErrorMessage(rawError: unknown): string | null {
        const message = typeof rawError === 'string'
            ? rawError
            : String((rawError as Record<string, unknown> | null)?.['message'] || '');

        if (message.includes('Fuera de la ventana de 24 horas debe indicar template_id aprobado')) {
            return 'Esta conversación venció por política de WhatsApp. Podés enviar un mensaje para retomarla.';
        }

        return null;
    }

    private async executeGetRequest(url: string, params?: Record<string, string>): Promise<ServiceResponse> {
        try {
            await this.updateToken();
            delete this.header['filters'];
            return await lastValueFrom(this.http.get<ServiceResponse>(url, {
                headers: this.header,
                params,
            }));
        } catch (error: unknown) {
            return this.checkError(error);
        }
    }

    private async listLegacyApprovedTemplates(idEmpresa: number): Promise<ServiceResponse> {
        try {
            await this.updateToken();
            this.header['filters'] = JSON.stringify([
                { field: 'id_empresa', type: 'id', value: idEmpresa },
                { field: 'estado', type: 'id', value: 2 },
                { field: 'activo', type: 'boolean', value: true },
            ]);
            return await lastValueFrom(this.http.get<ServiceResponse>(this.templateUrl, {
                headers: this.header,
                params: { page: 0, size: 200 },
            }));
        } catch (error: unknown) {
            return this.checkError(error);
        } finally {
            delete this.header['filters'];
        }
    }

    private buildConversationRequest(filters: WhatsappConversationFilter): { params: Record<string, string>; requestFilters: Record<string, unknown>[] } {
        const currentUserId = this.getCurrentUserId();
        const params: Record<string, string> = {
            page: String(filters.page || 0),
            size: String(filters.size || 20),
        };
        const requestFilters: Record<string, unknown>[] = [
            { field: 'id_empresa', type: 'id', value: filters.idEmpresa || this.getCurrentUserCompanyId() },
        ];

        if (currentUserId > 0) {
            requestFilters.push({ field: 'id_usuario', type: 'id', value: currentUserId });
        }

        if (filters.search) {
            requestFilters.push({ field: 'search', type: 'text', value: String(filters.search).trim() });
        }

        if (Number(filters.conversationId || 0) > 0) {
            requestFilters.push({ field: 'conversation_id', type: 'id', value: Number(filters.conversationId) });
        }

        const conversationStartFrom = this.formatFilterDate(filters.conversationStartFrom);
        if (conversationStartFrom) {
            requestFilters.push({ field: 'conversation_start_from', type: 'date', value: conversationStartFrom });
        }

        const conversationStartTo = this.formatFilterDate(filters.conversationStartTo);
        if (conversationStartTo) {
            requestFilters.push({ field: 'conversation_start_to', type: 'date', value: conversationStartTo });
        }

        if (filters.uiStatus) {
            requestFilters.push({ field: 'ui_status', type: 'text', value: filters.uiStatus });
        }

        if (filters.assignedUserId) {
            requestFilters.push({ field: 'assigned_user_id', type: 'id', value: filters.assignedUserId });
        }

        if (filters.onlyUnread) {
            requestFilters.push({ field: 'only_unread', type: 'boolean', value: true });
        }

        if (filters.onlyUnassigned) {
            requestFilters.push({ field: 'only_unassigned', type: 'boolean', value: true });
        }

        return { params, requestFilters };
    }

    private buildConversationDetailRequest(filters: WhatsappConversationDetailFilter): Record<string, unknown>[] {
        const currentUserId = this.getCurrentUserId();
        const requestFilters: Record<string, unknown>[] = [
            { field: 'id_empresa', type: 'id', value: filters.idEmpresa || this.getCurrentUserCompanyId() },
        ];

        if (currentUserId > 0) {
            requestFilters.push({ field: 'id_usuario', type: 'id', value: currentUserId });
        }

        if (filters.markAsRead) {
            requestFilters.push({ field: 'mark_as_read', type: 'boolean', value: true });
        }

        return requestFilters;
    }

    private normalizeConversationDetailFilters(options?: number | WhatsappConversationDetailFilter): WhatsappConversationDetailFilter {
        if (typeof options === 'number') {
            return {
                idEmpresa: options,
                markAsRead: true,
            };
        }

        return {
            idEmpresa: options?.idEmpresa,
            markAsRead: options?.markAsRead !== false,
        };
    }

    private normalizeConversationSummary(row: Record<string, unknown>): WhatsappConversationSummary {
        const metadata = this.extractRecord(row['metadata']);
        const externalHandoffStatusCode = this.toNullableNumber(row['external_handoff_status_code']) ?? WHATSAPP_EXTERNAL_HANDOFF_STATUS.NO_DERIVADA;
        const externalHandoffStatusLabel = this.toNullableString(
            row['external_handoff_status_label']
            || metadata?.['external_handoff_status_label']
        );
        const availableReplyTemplates = this.extractReplyTemplatesFromRaw(
            row['available_reply_templates']
            || metadata?.['available_reply_templates']
            || []
        );

        return {
            ...(row as unknown as WhatsappConversationSummary),
            status_label: String(row['status_label'] || '' || ''),
            contact_name: this.toNullableString(row['contact_name']),
            external_handoff_status_label: externalHandoffStatusCode === WHATSAPP_EXTERNAL_HANDOFF_STATUS.NO_DERIVADA
                ? null
                : externalHandoffStatusLabel || getWhatsappExternalHandoffStatusLabel(externalHandoffStatusCode),
            external_handoff_reason: this.toNullableString(row['external_handoff_reason']),
            external_handoff_target_phone: this.toNullableString(row['external_handoff_target_phone']),
            external_handoff_responsible_name: this.toNullableString(row['external_handoff_responsible_name']),
            external_handoff_responsible_user_id: this.toNullableNumber(row['external_handoff_responsible_user_id']),
            external_handoff_summary: this.toNullableString(row['external_handoff_summary']),
            external_handoff_at: (row['external_handoff_at'] as string | Date | null) || null,
            last_inbound_at: (row['last_inbound_at'] as string | Date | null) || this.toNullableDate(metadata?.['last_inbound_at']),
            conversation_window_expires_at: (row['conversation_window_expires_at'] as string | Date | null)
                || this.toNullableDate(metadata?.['conversation_window_expires_at'])
                || this.toNullableDate(metadata?.['window_expires_at']),
            is_outside_window: this.toNullableBoolean(row['is_outside_window'] ?? metadata?.['is_outside_window'] ?? metadata?.['outside_window']),
            is_within_window: this.toNullableBoolean(row['is_within_window'] ?? metadata?.['is_within_window'] ?? metadata?.['within_window']),
            reply_requires_template: this.toNullableBoolean(row['reply_requires_template'] ?? metadata?.['reply_requires_template'] ?? metadata?.['requires_template_reply']),
            can_send_free_reply: this.toNullableBoolean(row['can_send_free_reply'] ?? metadata?.['can_send_free_reply']),
            metadata,
            available_reply_templates: availableReplyTemplates,
            historial_nombre: this.toNullableString(row['historial_nombre']),
        };
    }

    private normalizeConversationMessage(message: Record<string, unknown>): WhatsappConversationMessage {
        return {
            ...(message as unknown as WhatsappConversationMessage),
            template_id: this.toNullableNumber(message['template_id']),
            template_code: this.toNullableString(message['template_code']),
            template_language: this.toNullableString(message['template_language']),
            metadata: this.extractRecord(message['metadata']),
        };
    }

    private normalizeConversationEvent(event: Record<string, unknown>): WhatsappConversationEvent {
        return event as unknown as WhatsappConversationEvent;
    }

    private extractReplyTemplatesFromRaw(rawTemplates: unknown): WhatsappConversationReplyTemplate[] {
        if (!Array.isArray(rawTemplates)) {
            return [];
        }

        return rawTemplates
            .map((template) => this.normalizeReplyTemplate(template as Record<string, unknown>))
            .filter((template) => Boolean(template.id));
    }

    private normalizeReplyTemplate(template: Record<string, unknown>): WhatsappConversationReplyTemplate {
        return {
            id: Number(template['id'] || 0),
            id_empresa: Number(template['id_empresa'] || this.getCurrentUserCompanyId()),
            nombre: String(template['nombre'] || template['template_name'] || 'Template sin nombre'),
            mensaje: String(template['mensaje'] || template['body_text'] || template['content'] || ''),
            estado: this.toNullableNumber(template['estado']),
            approve_status: this.toNullableString(template['approve_status']),
            language: this.toNullableString(template['language']) || 'es',
            meta_template_name: this.toNullableString(template['meta_template_name']),
            content_sid: this.toNullableString(template['content_sid']),
            url_imagen: this.toNullableString(template['url_imagen']),
        };
    }

    private normalizeAssignableUser(user: WhatsappAssignableUser): WhatsappAssignableUser {
        const codigo = String(user.codigo || '').trim();
        const nombre = String(user.nombre || '').trim();

        return {
            ...user,
            display_label: codigo ? `${codigo} - ${nombre}` : nombre,
        };
    }

    private extractRecord(value: unknown): Record<string, unknown> | null {
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
            return null;
        }

        return value as Record<string, unknown>;
    }

    private toNullableString(value: unknown): string | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        return String(value);
    }

    private toNullableNumber(value: unknown): number | null {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : value === 0 ? 0 : null;
    }

    private toNullableBoolean(value: unknown): boolean | null {
        if (typeof value === 'boolean') {
            return value;
        }

        if (typeof value === 'number') {
            return value === 1 ? true : value === 0 ? false : null;
        }

        if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase();
            if (['true', '1', 'si', 'sí', 'yes'].includes(normalized)) {
                return true;
            }

            if (['false', '0', 'no'].includes(normalized)) {
                return false;
            }
        }

        return null;
    }

    private toNullableDate(value: unknown): string | Date | null {
        if (!value) {
            return null;
        }

        return value as string | Date;
    }

    private formatFilterDate(value: string | Date | null | undefined): string | null {
        if (!value) {
            return null;
        }

        const dateValue = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(dateValue.getTime())) {
            return null;
        }

        const year = dateValue.getFullYear();
        const month = String(dateValue.getMonth() + 1).padStart(2, '0');
        const day = String(dateValue.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }

    private getCurrentUserId(): number {
        return Number(this.seguridadService.getCurrentUser()?.id || 0);
    }

    private getCurrentUserCompanyId(): number {
        return Number(this.seguridadService.getCurrentUser()?.id_empresa || 0);
    }
}
